import type { GeschaeftspartnerTyp } from "@/lib/kontakte";

/**
 * Letzte Suche der Kunden- bzw. Lieferantenliste.
 * Bleibt über den Wechsel in die Detailansicht erhalten und wird erst
 * geleert, wenn die Suche über das X zurückgesetzt wird.
 */
export interface GeschaeftspartnerListView {
  q: string;
  offset: number;
}

const STORAGE_PREFIX = "eh.kontakte.list.";

export interface ListViewStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const EMPTY_VIEW: GeschaeftspartnerListView = { q: "", offset: 0 };

export function geschaeftspartnerListStorageKey(typ: GeschaeftspartnerTyp): string {
  return `${STORAGE_PREFIX}${typ}`;
}

function browserStorage(): ListViewStorage | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

function normalizeOffset(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return 0;
  return Math.min(Math.floor(value), 1_000_000);
}

export function readGeschaeftspartnerListView(
  typ: GeschaeftspartnerTyp,
  storage: ListViewStorage | null = browserStorage(),
): GeschaeftspartnerListView {
  if (!storage) return EMPTY_VIEW;
  try {
    const raw = storage.getItem(geschaeftspartnerListStorageKey(typ));
    if (!raw) return EMPTY_VIEW;
    const parsed = JSON.parse(raw) as { q?: unknown; offset?: unknown };
    const q = typeof parsed.q === "string" ? parsed.q : "";
    if (!q.trim()) return EMPTY_VIEW;
    return { q, offset: normalizeOffset(parsed.offset) };
  } catch {
    return EMPTY_VIEW;
  }
}

/**
 * Anzeige-Modus der Liste: alphabetisch oder „Top zuerst“ (nach Aktivität).
 * Wird als Präferenz dauerhaft im localStorage gehalten.
 */
export type GeschaeftspartnerListMode = "alpha" | "top";

const MODE_STORAGE_PREFIX = "eh.kontakte.listmode.";

function preferenceStorage(): ListViewStorage | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function readGeschaeftspartnerListMode(
  typ: GeschaeftspartnerTyp,
  storage: ListViewStorage | null = preferenceStorage(),
): GeschaeftspartnerListMode {
  if (!storage) return "alpha";
  try {
    const raw = storage.getItem(`${MODE_STORAGE_PREFIX}${typ}`);
    return raw === "top" ? "top" : "alpha";
  } catch {
    return "alpha";
  }
}

export function writeGeschaeftspartnerListMode(
  typ: GeschaeftspartnerTyp,
  mode: GeschaeftspartnerListMode,
  storage: ListViewStorage | null = preferenceStorage(),
): void {
  if (!storage) return;
  try {
    const key = `${MODE_STORAGE_PREFIX}${typ}`;
    if (mode === "alpha") storage.removeItem(key);
    else storage.setItem(key, mode);
  } catch {
    // localStorage kann blockiert sein.
  }
}

export function writeGeschaeftspartnerListView(
  typ: GeschaeftspartnerTyp,
  view: GeschaeftspartnerListView,
  storage: ListViewStorage | null = browserStorage(),
): void {
  if (!storage) return;
  const key = geschaeftspartnerListStorageKey(typ);
  try {
    if (!view.q.trim()) {
      storage.removeItem(key);
      return;
    }
    storage.setItem(
      key,
      JSON.stringify({ q: view.q, offset: normalizeOffset(view.offset) }),
    );
  } catch {
    // sessionStorage kann in privaten Kontexten blockiert sein.
  }
}
